import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../services/api';

export default function Clients() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    status: 'закупает регулярный',
    generalInfo: '',
    responsibleUserId: null
  });

  const statuses = [
    { value: 'закупает регулярный', label: 'закупает регулярный', color: '#4CAF50', group: 'green' },
    { value: 'закупает редко', label: 'закупает редко', color: '#4CAF50', group: 'green' },
    { value: 'порулонщик', label: 'порулонщик', color: '#FF9800', group: 'yellow' },
    { value: 'отработан, у конкурентов', label: 'отработан, у конкурентов', color: '#FF9800', group: 'yellow' },
    { value: 'общаемся, но не покупает', label: 'общаемся, но не покупает', color: '#FF9800', group: 'yellow' },
    { value: 'не контактировали', label: 'не контактировали', color: '#F44336', group: 'red' },
    { value: 'отработан, не с рынка', label: 'отработан, не с рынка', color: '#F44336', group: 'red' }
  ];

  const getStatusMeta = (statusValue) => {
    const found = statuses.find(s => s.value === statusValue);
    if (found) return found;
    if (!statusValue) return { label: '-', color: '#9e9e9e', group: 'unknown' };
    return { label: String(statusValue), color: '#9e9e9e', group: 'unknown' };
  };

  const statusGroupOrder = {
    green: 0,
    yellow: 1,
    red: 2,
    unknown: 3
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clientsRes = await clientsAPI.getAll();
      setClients(clientsRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        companyName: client.companyName,
        status: client.status,
        generalInfo: client.generalInfo || '',
        responsibleUserId: client.responsibleUserId || null
      });
    } else {
      setEditingClient(null);
      setFormData({
        companyName: '',
        status: statuses[0]?.value || 'закупает регулярный',
        generalInfo: '',
        responsibleUserId: null
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingClient(null);
  };

  const handleSave = async () => {
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
      } else {
        await clientsAPI.create(formData);
      }
      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError('Ошибка сохранения');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить клиента?')) {
      try {
        await clientsAPI.delete(id);
        await loadData();
      } catch (err) {
        setError('Ошибка удаления');
      }
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.companyName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    if (statusFilter !== 'all') {
      return a.companyName.localeCompare(b.companyName, 'ru');
    }

    const aMeta = getStatusMeta(a.status);
    const bMeta = getStatusMeta(b.status);
    const aGroup = statusGroupOrder[aMeta.group] ?? statusGroupOrder.unknown;
    const bGroup = statusGroupOrder[bMeta.group] ?? statusGroupOrder.unknown;

    if (aGroup !== bGroup) return aGroup - bGroup;
    return a.companyName.localeCompare(b.companyName, 'ru');
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '2rem' } }}>
          Клиенты
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            size="small"
            variant="outlined"
          >
            {!isMobile && 'Главная'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOpenDialog()}
            size="small"
            sx={{ minWidth: 'auto', px: { xs: 1, sm: 2 } }}
          >
            {isMobile ? <AddIcon /> : <>
              <AddIcon sx={{ mr: 1 }} />
              Добавить
            </>}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              placeholder="Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Статус"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Все</MenuItem>
              {statuses.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Mobile: Cards */}
      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {sortedClients.map((client) => (
            <Card
              key={client.id}
              sx={{
                width: '100%',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1rem', flex: 1, pr: 1 }}>
                    {client.companyName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(client); }} sx={{ p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(client.id); }} sx={{ p: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Chip
                  label={getStatusMeta(client.status).label}
                  size="small"
                  sx={{
                    bgcolor: getStatusMeta(client.status).color,
                    color: 'white',
                    mb: 0.5,
                    height: 20,
                    fontSize: '0.75rem'
                  }}
                />
                {client.generalInfo && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
                    {client.generalInfo.substring(0, 50)}...
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
          {sortedClients.length === 0 && (
            <Card>
              <CardContent>
                <Typography color="text.secondary" align="center">
                  Нет клиентов
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        /* Desktop: Table */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Информация</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedClients.map((client) => (
                <TableRow
                  key={client.id}
                  hover
                  onClick={() => navigate(`/clients/${client.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{client.companyName}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusMeta(client.status).label}
                      size="small"
                      sx={{
                        bgcolor: getStatusMeta(client.status).color,
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>{client.generalInfo || '-'}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => handleOpenDialog(client)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(client.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredClients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">Нет клиентов</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { width: { xs: '100%', sm: '500px' }, m: { xs: 0, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingClient ? 'Редактировать' : 'Добавить'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Название компании"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              required
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Статус"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              fullWidth
              size="small"
            >
              {statuses.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Информация"
              value={formData.generalInfo}
              onChange={(e) => setFormData({ ...formData, generalInfo: e.target.value })}
              multiline
              rows={3}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} size="medium">
            Отмена
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.companyName}
            size="medium"
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
